import {computed, effect, inject, Injectable, OnDestroy, signal} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  forkJoin,
  map,
  Observable, of,
  Subject,
  switchMap,
  takeUntil,
  tap
} from "rxjs";
import {MalfunctionStatus, Report} from "../../../shared/interfaces/report";
import {PAGE_SIZE, TableType} from "../../dashboard/components/report-table/table-config";
import {UserService} from "../../../services/user.service";
import {ApiService} from "../../../services/api.service";
import {CookieService} from "ngx-cookie-service";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {Userrole} from "../../../shared/interfaces/user";
import {FilterParams} from "../../../shared/components/filter-interface/filter-interface.component";

export interface SearchResult {
  "production_line": {
    "id": "uuid",
    "name": "string"
  },
  "workplace": {
    "id": "uuid",
    "name": "string"
  },
  "category": {
    "id": "uuid",
    "name": "string"
  },
  "malfunction": {
    "id": "uuid",
    "name": "string",
    "description": "string",
    "created_at": "timestamp",
    "updated_at": "timestamp"
  },
  "solution": {
    "id": "uuid",
    "name": "string",
    "description": "string",
    "created_at": "timestamp",
    "updated_at": "timestamp"
  }
}

interface SearchResultState {
  knowledgeBaseSearchResults: SearchResult[];
  reportSearchResults: TablesState;
}

export interface TableState {
  currentPage: number;
  totalPages: number;
  data: Report[];
}

export interface TablesState {
  activeReports: TableState;
  solvedReports: TableState;
  escalatedReports: TableState
}

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private apiService = inject(ApiService);

  // state
  private searchTerm = signal<string | null>(null);
  private filterValues = signal<FilterParams | null>(null);
  private productionLineFilter = signal<string | null>(null);
  private workplaceFilter = signal<string | null>(null);
  private pageStates = {
    [TableType.ACTIVE]: signal(1),
    [TableType.SOLVED]: signal(1),
    [TableType.ESCALATED]: signal(1)
  }

  private readonly initialState: SearchResultState = {
    knowledgeBaseSearchResults: [],
    reportSearchResults: {
      activeReports: {currentPage: 1, totalPages: 1, data: []},
      solvedReports: {currentPage: 1, totalPages: 1, data: []},
      escalatedReports: {currentPage: 1, totalPages: 1, data: []}
    }
  }

  private searchResultState = signal<SearchResultState>(this.initialState)

  // computed properties
  readonly knowledgeBaseResults = computed(() => this.searchResultState().knowledgeBaseSearchResults);
  readonly state = computed(() => this.searchResultState().reportSearchResults);

  constructor() {
    combineLatest([
      toObservable(this.searchTerm),
      toObservable(this.filterValues),
      toObservable(this.productionLineFilter),
      toObservable(this.workplaceFilter),
      toObservable(this.pageStates[TableType.ACTIVE]),
      toObservable(this.pageStates[TableType.SOLVED]),
      toObservable(this.pageStates[TableType.ESCALATED])
    ]).pipe(
      switchMap(([searchTerm, filterValues, productionLineFilter, workplaceFilter, pageActive, pageSolved, pageEscalated]) =>
        forkJoin({
          activeReports: this.apiService.getFilteredReports({
            ...this.createFilterConfig(pageActive, searchTerm, filterValues, productionLineFilter, workplaceFilter),
            statusFilter: [MalfunctionStatus.RECORDED, MalfunctionStatus.ESCALATED]
          }),
          solvedReports: this.apiService.getFilteredReports({
            ...this.createFilterConfig(pageSolved, searchTerm, filterValues, productionLineFilter, workplaceFilter),
            statusFilter: [MalfunctionStatus.SOLVED]
          })
        }).pipe(
          map(({activeReports, solvedReports}) => ({
            pageActive,
            pageSolved,
            pageEscalated,
            activeReports,
            solvedReports
          }))
        )
      ),
      catchError(error => {
        console.error('Error fetching reports:', error);
        return of(null);
      }),
      takeUntilDestroyed()
    ).subscribe(result => {
      if (!result) return;
      const {
        pageActive,
        pageSolved,
        pageEscalated,
        activeReports,
        solvedReports
      } = result;

      const escalatedReports = activeReports?.reportData?.filter(
        (report: Report) => report.status === MalfunctionStatus.ESCALATED
      ) ?? [];

      const totalPagesActive = Math.ceil((activeReports?.totalCount || 0) / PAGE_SIZE);
      const totalPagesSolved = Math.ceil((solvedReports?.totalCount || 0) / PAGE_SIZE);
      const totalPagesEscalated = Math.ceil(escalatedReports.length / PAGE_SIZE);

      this.searchResultState.update((state) => ({
        ...state,
        reportSearchResults: {
          activeReports: {
            data: activeReports?.reportData ?? [],
            currentPage: pageActive,
            totalPages: totalPagesActive
          },
          solvedReports: {
            data: solvedReports?.reportData ?? [],
            currentPage: pageSolved,
            totalPages: totalPagesSolved
          },
          escalatedReports: {
            data: escalatedReports,
            currentPage: pageEscalated,
            totalPages: totalPagesEscalated
          }
        }
      }));
    });
  }

  // setters for search parameters
  setSearchTerm(term: string | null) {
    this.searchTerm.set(term);
  }

  setFilterValues(filters: FilterParams | null) {
    this.filterValues.set(filters);
  }

  setProductionLineFilter(id: string | null) {
    this.productionLineFilter.set(id);
  }

  setWorkplaceFilter(id: string | null) {
    this.workplaceFilter.set(id);
  }

  setPage(type: TableType, page: number) {
    this.pageStates[type].set(page);
  }

  private createFilterConfig(page: number, searchTerm: string | null, filterValues: FilterParams | null, productionLineFilter: string | null, workplaceFilter: string | null) {
    return {
      searchText: searchTerm,
      rangeStart: (page - 1) * PAGE_SIZE,
      rangeEnd: page * PAGE_SIZE,
      startDate: filterValues?.startDate,
      endDate: filterValues?.endDate,
      startTime: filterValues?.startTime,
      endTime: filterValues?.endTime,
      minDuration: filterValues?.minDuration,
      maxDuration: filterValues?.maxDuration,
      productionLineFilter: productionLineFilter ? [productionLineFilter] : undefined,
      workplaceFilter: workplaceFilter ? [workplaceFilter] : undefined
    };
  }

  private searchKnowledgeBase(searchParam: string): Observable<SearchResult[]> {
    return this.apiService.searchKnowledgeBase(
      searchParam,
      this.productionLineFilter()
    ).pipe(
      tap(results => this.searchResultState.update((state) => ({
          ...state,
          knowledgeBaseSearchResults: results
        })),
        catchError(error => {
          console.error('Error fetching knowledge base search results:', error);
          return of([]);
        })
      )
    )
  }

  triggerKnowledgeBaseSearch(searchParam: string): void {
    this.searchKnowledgeBase(searchParam).subscribe();
  }

  resetSearchResults() {
    this.searchResultState.set(this.initialState);
  }
}
